import { referralToken, clearReferral } from "@/lib/referrals";
import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [ready, setReady] = useState(false);
  const lastPresenceUpdate = useRef(0);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(null);
    } finally {
      setReady(true);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    if (!ready || user?.role !== "expert") return undefined;

    const heartbeat = () => {
      if (document.visibilityState !== "visible") return;
      const now = Date.now();
      if (now - lastPresenceUpdate.current < 60_000) return;
      lastPresenceUpdate.current = now;
      api.post("/expert/presence").catch(() => {});
    };

    heartbeat();
    const timer = window.setInterval(heartbeat, 60_000);
    const onVisibilityChange = () => heartbeat();
    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("focus", heartbeat);
    window.addEventListener("pointerdown", heartbeat, { passive: true });
    window.addEventListener("keydown", heartbeat);

    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("focus", heartbeat);
      window.removeEventListener("pointerdown", heartbeat);
      window.removeEventListener("keydown", heartbeat);
      lastPresenceUpdate.current = 0;
    };
  }, [ready, user?.id, user?.role]);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data.token) localStorage.setItem("en_token", data.token);
      await refresh();
      return { ok: true, user: data.user };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) };
    }
  };

  const register = async (payload) => {
    try {
      const { data } = await api.post("/auth/register", { ...payload, referral_token: await referralToken() });
      if (data.token) localStorage.setItem("en_token", data.token);
      clearReferral();
      setUser(data.user);
      setReady(true);
      return { ok: true, user: data.user };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) };
    }
  };

  const logout = () => {
    // Start server-side logout while clearing the local session immediately.
    // The user must always be able to leave the pending approval screen even
    // if the network request is slow or unavailable.
    void api.post("/auth/logout").catch(() => {});
    localStorage.removeItem("en_token");
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, ready, login, register, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}

